import { useRef, useState, type MutableRefObject } from "react";
import { ArrowRight, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
  CoachLeaderLongitudinalCoverage,
  CoachLeaderLongitudinalEpisode,
  CoachLeaderLongitudinalRead,
} from "@/lib/coachLeaderDetail/buildCoachLeaderLongitudinalRead";

export interface CoachLeaderLongitudinalInsightProps {
  readonly read: CoachLeaderLongitudinalRead;
  readonly onOpenSource: (episode: CoachLeaderLongitudinalEpisode) => void;
  readonly onAskCoachVeera: (invoker: HTMLButtonElement) => void;
  readonly onAddQuestion: (question: string) => void;
  readonly onRetry?: () => void;
}

function formatMeetingDate(value: string | null) {
  if (!value) return "date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function stateLabel(state: Extract<CoachLeaderLongitudinalRead, { finding: unknown }>["state"]) {
  switch (state) {
    case "historical_signal": return "Historical signal";
    case "emerging_finding": return "Emerging finding";
    case "evidence_backed_finding": return "Evidence-backed finding";
  }
}

function coverageCopy(coverage: CoachLeaderLongitudinalCoverage) {
  if (coverage.aggregateSource === "compatibility_only_v1") {
    return "Based on existing coaching history";
  }
  if (coverage.sourceVerifiedMeetingCount > 0) {
    const meetingLabel = coverage.sourceVerifiedMeetingCount === 1 ? "exact meeting" : "exact meetings";
    return `Based on aggregate history and ${coverage.sourceVerifiedMeetingCount} ${meetingLabel}`;
  }
  if (coverage.authorizedSourceReadiness === "unavailable") {
    return "Based on aggregate history; exact source verification is temporarily unavailable";
  }
  return "Based on aggregate history; exact source coverage is still developing";
}

function buildingCoverageCopy(historicalMeetingCount: number | null) {
  if (typeof historicalMeetingCount !== "number" || !Number.isFinite(historicalMeetingCount) || historicalMeetingCount < 0) {
    return "Recorded coaching sessions will add context here as they are processed.";
  }

  const sessionLabel = historicalMeetingCount === 1 ? "session is" : "sessions are";
  return `${historicalMeetingCount} recorded coaching ${sessionLabel} contributing context.`;
}

function episodeName(episode: CoachLeaderLongitudinalEpisode) {
  const kind = episode.polarity === "support" ? "supporting" : "challenging";
  return `Review ${kind} meeting ${episode.title} from ${formatMeetingDate(episode.occurredAt)}`;
}

type EpisodeGroupProps = Readonly<{
  label: "Supporting meetings" | "Challenging meetings";
  episodes: readonly CoachLeaderLongitudinalEpisode[];
  selectedEpisode: CoachLeaderLongitudinalEpisode | null;
  setSelectedEpisodeId: (episodeId: string | null) => void;
  episodeButtonsRef: MutableRefObject<Map<string, HTMLButtonElement>>;
}>;

function episodeKey(episode: CoachLeaderLongitudinalEpisode) {
  return `${episode.source.identity.sourceType}:${episode.source.identity.sourceId}`;
}

function emphasizedEpisodes(
  supporting: readonly CoachLeaderLongitudinalEpisode[],
  challenging: readonly CoachLeaderLongitudinalEpisode[],
) {
  const selected = [supporting[0], challenging[0]].filter(
    (episode): episode is CoachLeaderLongitudinalEpisode => Boolean(episode),
  );
  if (selected.length === 2) return selected;

  const selectedKeys = new Set(selected.map(episodeKey));
  for (const episode of [...supporting, ...challenging]) {
    if (selected.length === 2) break;
    const key = episodeKey(episode);
    if (selectedKeys.has(key)) continue;
    selected.push(episode);
    selectedKeys.add(key);
  }
  return selected;
}

function EpisodeGroup({
  label,
  episodes,
  selectedEpisode,
  setSelectedEpisodeId,
  episodeButtonsRef,
}: EpisodeGroupProps) {
  if (episodes.length === 0) return null;
  return (
    <div role="group" aria-label={label} className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{label}</h3>
      <div className="flex flex-wrap gap-2">
        {episodes.map((episode) => {
          const key = episodeKey(episode);
          return (
            <button
              key={key}
              ref={(node) => {
                if (node) episodeButtonsRef.current.set(key, node);
                else episodeButtonsRef.current.delete(key);
              }}
              type="button"
              aria-label={episodeName(episode)}
              aria-expanded={selectedEpisode ? episodeKey(selectedEpisode) === key : false}
              className="rounded-full border border-[#d8c7ae]/80 bg-white px-3 py-2 text-left text-sm font-medium text-[#4c382c] transition hover:bg-[#f5ece1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c98c56]/60 dark:border-[#d9c8ae]/25 dark:bg-white/[0.04] dark:text-[#f4dfc7]"
              onClick={() => setSelectedEpisodeId(selectedEpisode && episodeKey(selectedEpisode) === key ? null : key)}
            >
              {episode.title} · {formatMeetingDate(episode.occurredAt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CoachLeaderLongitudinalInsight({
  read,
  onOpenSource,
  onAskCoachVeera,
  onAddQuestion,
  onRetry,
}: CoachLeaderLongitudinalInsightProps) {
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(null);
  const [allMeetingsOpen, setAllMeetingsOpen] = useState(false);
  const episodeButtonsRef = useRef(new Map<string, HTMLButtonElement>());

  const findingEpisodes = read.state === "historical_signal"
    || read.state === "emerging_finding"
    || read.state === "evidence_backed_finding"
    ? [...read.supportingEpisodes, ...read.challengingEpisodes]
    : [];
  const displayedEpisodes = allMeetingsOpen
    ? findingEpisodes
    : read.state === "historical_signal"
      || read.state === "emerging_finding"
      || read.state === "evidence_backed_finding"
      ? emphasizedEpisodes(read.supportingEpisodes, read.challengingEpisodes)
      : [];
  const supportingEpisodes = displayedEpisodes.filter((episode) => episode.polarity === "support");
  const challengingEpisodes = displayedEpisodes.filter((episode) => episode.polarity === "contextual_challenge");
  const selectedEpisode = findingEpisodes.find((episode) => episodeKey(episode) === selectedEpisodeId) ?? null;

  if (read.state === "revoked") return null;

  if (read.state === "loading") {
    return (
      <section aria-label="Longitudinal context" className="mt-8 rounded-2xl border border-border/60 bg-muted/20 px-5 py-4">
        <p role="status" aria-label="Loading longitudinal context" className="animate-pulse text-sm text-muted-foreground">
          Preparing longitudinal context...
        </p>
      </section>
    );
  }

  if (read.state === "unavailable") {
    return (
      <section aria-label="Longitudinal context" className="mt-8 space-y-3 rounded-2xl border border-border/60 bg-muted/20 px-5 py-4">
        <p role="status" className="text-sm text-muted-foreground">Longitudinal context is unavailable right now.</p>
        {read.retryable && onRetry ? (
          <Button type="button" variant="outline" onClick={onRetry}>Try loading longitudinal context again</Button>
        ) : null}
      </section>
    );
  }

  if (read.state === "insufficient_history") {
    const sessionCoverage = buildingCoverageCopy(read.coverage.historicalMeetingCount);
    return (
      <section aria-label="Longitudinal context" className="mt-8 overflow-hidden rounded-[1.8rem] border border-[#d7c5ac]/80 bg-[#fbf8f3] shadow-[0_28px_76px_-58px_rgba(50,28,69,0.62)] dark:border-[#d9c8ae]/20 dark:bg-[#140f22]">
        <div className="grid gap-7 px-5 py-6 sm:px-7 lg:grid-cols-[minmax(0,0.86fr)_minmax(23rem,1.14fr)] lg:items-end lg:gap-10 lg:py-8">
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7f5b43] dark:text-[#c9ad87]">What Coach Veera is noticing over time</p>
            <div className="space-y-2">
              <h2 className="max-w-xl font-serif text-2xl font-semibold tracking-tight text-[#251837] sm:text-[1.72rem] dark:text-[#f7f0e8]">Coach Veera is building the picture over time</h2>
              <p className="max-w-xl text-sm leading-6 text-muted-foreground">The next useful observation will come from comparison, not from a single meeting. Coach Veera waits for separate recorded sessions to make a meaningful connection.</p>
            </div>
            <div className="flex items-start gap-3 border-l-2 border-[#b48760]/70 pl-3.5 dark:border-[#c9ad87]/70">
              <Sparkles aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-[#7f5b43] dark:text-[#c9ad87]" />
              <p className="text-sm leading-6 text-[#594235] dark:text-[#e9d8c3]">{sessionCoverage}</p>
            </div>
          </div>

          <div aria-label="Longitudinal context is building" className="relative rounded-[1.35rem] border border-[#38254b]/10 bg-[#2a183a] px-5 py-5 text-[#f8f2ea] shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] dark:border-[#e6d5bd]/15">
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-10 top-[3.3rem] h-px bg-gradient-to-r from-[#c68b5b]/70 via-[#e9d5aa]/70 to-[#8d77c7]/50" />
            <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 size-56 rounded-full bg-[#8f6fbb]/20 blur-3xl" />
            <p className="relative text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[#dbc6a6]">A longitudinal read forms across separate moments</p>
            <ol aria-label="How longitudinal context develops" className="relative mt-6 grid gap-5 sm:grid-cols-3">
              <li className="space-y-2">
                <span className="flex size-8 items-center justify-center rounded-full border border-[#e7c98c]/80 bg-[#4e355f] text-xs font-semibold text-[#fff4df]">01</span>
                <p className="text-sm font-semibold text-[#fff8ef]">Recorded coaching</p>
                <p className="text-xs leading-5 text-[#d9cde0]">A session adds its own context.</p>
              </li>
              <li className="space-y-2">
                <span className="flex size-8 items-center justify-center rounded-full border border-dashed border-[#c7b5d7]/80 bg-[#3b284c] text-xs font-semibold text-[#f0e8f6]">02</span>
                <p className="text-sm font-semibold text-[#fff8ef]">Separate sessions create the comparison</p>
                <p className="text-xs leading-5 text-[#d9cde0]">Context can strengthen, complicate, or challenge an earlier read.</p>
              </li>
              <li className="space-y-2">
                <span className="flex size-8 items-center justify-center rounded-full border border-dashed border-[#c7b5d7]/80 bg-[#3b284c] text-xs font-semibold text-[#f0e8f6]">03</span>
                <p className="text-sm font-semibold text-[#fff8ef]">A coach question earns its place here</p>
                <p className="text-xs leading-5 text-[#d9cde0]">Only then will Coach Veera surface one evidence-bound observation.</p>
              </li>
            </ol>
            <div className="relative mt-5 flex items-center gap-2 border-t border-white/15 pt-4 text-xs text-[#d9cde0]">
              <span>See the connection when the evidence is ready</span>
              <ArrowRight aria-hidden="true" className="size-3.5 text-[#e7c98c]" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Longitudinal context"
      className="mt-8 space-y-5 rounded-[1.65rem] border border-[#d8c7ae]/70 bg-[#fbf8f3] px-5 py-6 shadow-[0_24px_70px_-54px_rgba(76,54,28,0.42)] sm:px-7 dark:border-[#d9c8ae]/20 dark:bg-[#140f22]"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !selectedEpisode) return;
        event.preventDefault();
        const selectedKey = episodeKey(selectedEpisode);
        setSelectedEpisodeId(null);
        requestAnimationFrame(() => episodeButtonsRef.current.get(selectedKey)?.focus());
      }}
    >
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7f5b43] dark:text-[#c9ad87]">What Coach Veera is noticing over time</p>
        <p className="text-sm font-semibold text-[#7f5b43] dark:text-[#c9ad87]">{stateLabel(read.state)}</p>
        <h2 className="font-serif text-2xl font-semibold tracking-tight text-[#251837] dark:text-[#f7f0e8]">{read.finding.headline}</h2>
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">{read.finding.explanation}</p>
        <p className="text-sm leading-6 text-muted-foreground">{coverageCopy(read.coverage)}</p>
      </div>

      {findingEpisodes.length > 0 ? (
        <div className="space-y-4">
          <EpisodeGroup
            label="Supporting meetings"
            episodes={supportingEpisodes}
            selectedEpisode={selectedEpisode}
            setSelectedEpisodeId={setSelectedEpisodeId}
            episodeButtonsRef={episodeButtonsRef}
          />
          <EpisodeGroup
            label="Challenging meetings"
            episodes={challengingEpisodes}
            selectedEpisode={selectedEpisode}
            setSelectedEpisodeId={setSelectedEpisodeId}
            episodeButtonsRef={episodeButtonsRef}
          />
          {findingEpisodes.length > 2 ? (
            <Button
              type="button"
              variant="ghost"
              aria-expanded={allMeetingsOpen}
              onClick={() => {
                setAllMeetingsOpen((open) => !open);
                if (allMeetingsOpen) setSelectedEpisodeId(null);
              }}
            >
              {allMeetingsOpen ? "Show fewer exact meetings" : "View all exact meetings"}
            </Button>
          ) : null}
        </div>
      ) : null}

      {selectedEpisode ? (
        <div className="space-y-3 rounded-2xl border border-[#d8c7ae]/70 bg-white/80 p-4 dark:border-[#d9c8ae]/20 dark:bg-white/[0.04]">
          <div className="space-y-1">
            <h3 className="font-semibold text-foreground">{selectedEpisode.title}</h3>
            <p className="text-sm text-muted-foreground">{formatMeetingDate(selectedEpisode.occurredAt)}</p>
          </div>
          <Button type="button" variant="outline" aria-label={`Open source for ${selectedEpisode.title}`} onClick={() => onOpenSource(selectedEpisode)}>
            Open source
          </Button>
        </div>
      ) : null}

      <div className="space-y-3 border-t border-[#d8c7ae]/70 pt-5 dark:border-[#d9c8ae]/20">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-foreground">One possible question</h3>
          <p className="text-sm leading-6 text-muted-foreground">{read.finding.question}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => onAddQuestion(read.finding.question)}>Add question to next session</Button>
          <Button type="button" variant="outline" onClick={(event) => onAskCoachVeera(event.currentTarget)}>Ask Coach Veera</Button>
        </div>
      </div>
    </section>
  );
}
